import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, throwError, of, map } from 'rxjs';
import { switchMap, catchError, finalize } from 'rxjs/operators';
import { MemberAccess } from '../models/common/member-access.model';
import { environment } from 'src/environments/environment';

import { UploadedProfileImage, UserProfileModel, UserTokenInfo } from '../models/repository/user-profile.model';
import { UserModel } from '../models/vet-users/user.model';
import { SpinnerService } from '../shared-new/services/spinner.service';

@Injectable({
  providedIn: 'root',
})
export class UserService {
  public baseUrl = environment.api2BaseUrl;
  public baseAuthUrl = environment.apiAuthUrl;
  public onError: BehaviorSubject<string> = new BehaviorSubject('');
  public serviceParameter = {
    'Content-Type': 'application/json; charset=utf-8',
    Accept: 'application/json',
    Authorization: '',
  };

  public constructor(private readonly http: HttpClient, private spinnerService: SpinnerService) {}

  public postMemberProfile(user: FormData): Observable<UserProfileModel> {
    return this.http.post<UserProfileModel>(`${this.baseAuthUrl}/members/profile`, user).pipe(
      catchError((error: Error) => {
        //
        this.onError.next(error.message);
        return throwError(error);
      })
    );
  }
  public postUserProfile(user: FormData): Observable<UserProfileModel> {
    return this.http.post<UserProfileModel>(`${this.baseAuthUrl}/users/profile`, user).pipe(
      catchError((error: Error) => {
        //
        this.onError.next(error.message);
        return throwError(error);
      })
    );
  }
  public postVetProfile(user: FormData): Observable<any> {
    this.spinnerService.addRequest('postProfile');
    return this.http.post<UserProfileModel>(`${this.baseAuthUrl}/users/profile`, user).pipe(
      catchError((error: Error) => {
        //
        this.onError.next(error.message);
        return throwError(error);
      }), finalize(() => this.spinnerService.popRequest('postProfile'))
    );
  }
  public uploadMemberProfile(formData: FormData): Observable<UploadedProfileImage> {
    return this.http.post<UploadedProfileImage>(`${this.baseAuthUrl}/members/savememberimage`, formData).pipe(
      catchError((error: Error) => {
        //
        this.onError.next(error.message);
        return throwError(error);
      })
    );
  }

 

 
  public getMemberProfile(memberId?: number,userId?: number): Observable<UserProfileModel> {
    const url = memberId ? `${this.baseAuthUrl}/members/${memberId}/${userId}/profile` : `${this.baseAuthUrl}/members/profile`;
    return this.http.get<UserProfileModel>(
      url
    ).pipe(
      catchError((error: Error) => {
        //
        this.onError.next(error.message);
        return throwError(error);
      }));
  }
  public getMemberAccess(memberId: number,saleId: number): Observable<MemberAccess> {
    const url = `${this.baseUrl}/api/member/${memberId}/${saleId}/saleMemberAccess`;
    return this.http.get<MemberAccess>(
      url
    ).pipe(
      catchError((error: Error) => {
        //
        this.onError.next(error.message);
        return throwError(error);
      }));
  }
  public getReviewProfile(memberId?: number, userId?: number): Observable<UserProfileModel> {
    const url = memberId ? `${this.baseAuthUrl}/members/${memberId}/${userId}/reviewProfile` : `${this.baseAuthUrl}/members/profile`;
    return this.http.get<UserProfileModel>(
      url
    ).pipe(
      catchError((error: Error) => {
        //
        this.onError.next(error.message);
        return throwError(error);
      }));
  }
  public getUserProfile(memberId?: number, userId?: number): Observable<UserProfileModel> {
    const url = memberId ? `${this.baseAuthUrl}/members/${memberId}/${userId}/profile` : `${this.baseAuthUrl}/users/profile`;
    return this.http.get<UserProfileModel>(
      url
    ).pipe(
      catchError((error: Error) => {
        //
        this.onError.next(error.message);
        return throwError(error);
      }));
  }
  getUsers(): Observable<UserModel[]> {
    const endpoint = `${this.baseAuthUrl}/users`;
    return this.http.get<UserModel[]>(endpoint).pipe(
      catchError((error) => {
        // Handle error, log it, emit an error event, etc.
        return throwError(error);
      })
    );
  }
  getUser(userId: number): Observable<UserModel> {
    const endpoint = `${this.baseAuthUrl}/${userId}/user`;
    return this.http.get<UserModel>(endpoint).pipe(
      catchError((error) => {
        // Handle error, log it, emit an error event, etc.
        return throwError(error);
      })
    );
  }

  saveUserAndNotifications(
    user: UserModel
  ): Observable<boolean> {
    return this.saveUser(user).pipe(
      switchMap((userId: number): Observable<boolean> => {
        if (userId > 0) {
          return this.saveUserNotificationTypes(userId, user.notificationSettings);
        }
        return of(false); // explicitly Observable<boolean>
      }),
      catchError((error) => {
        this.onError.next(error.message);
        return throwError(() => error);
      })
    );
  }

  saveUser(user: UserModel): Observable<number> {
    const endpoint = `${this.baseAuthUrl}/users`;

    if (user.userId && user.userId > 0) {
      // Update existing user
      return this.http.put<number>(endpoint, user).pipe(
        catchError((error) => throwError(() => error))
      );
    } else {
      // Create new user
      return this.http.post<number>(endpoint, user).pipe(
        catchError((error) => throwError(() => error))
      );
    }
  }

  saveUserNotificationTypes(
    userId: number,
    notificationSettings: { [key: number]: boolean }
  ): Observable<boolean> {
    return this.http.post<boolean>(
      `${this.baseUrl}/api/member/${userId}/SaveUserNotificationType`,
      notificationSettings
    ).pipe(
      catchError((error: Error) => {
        this.onError.next(error.message);
        return throwError(() => error);
      })
    );
  }

}
